import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getGigs } from '../../services/gig.service';
import { getCategories } from '../../services/category.service';
import Header from '../../components/Header'
import Footer from '../../components/Footer'
import CustomQuoteBanner from '../../components/CustomQuoteBanner'
import Actions from '../../components/Actions'


const CategoryShow = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeHoverCard, setActiveHoverCard] = useState(null);
  const [slidingIndexes, setSlidingIndexes] = useState({});
  const itemsPerPage = 12;

  // Format category slug to display name
  const formatCategoryName = (slug) => {
    if (!slug) return 'All Services';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const categoryTitle = formatCategoryName(category);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search change
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [filters, setFilters] = useState({
    minPrice: null,
    maxPrice: null,
    keywords: null
  });

  // Listen for URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setFilters({
        minPrice: urlParams.get('minPrice'),
        maxPrice: urlParams.get('maxPrice'),
        keywords: urlParams.get('keywords')
      });
      
      const urlSearch = urlParams.get('search') || '';
      if (urlSearch !== searchTerm) {
        setSearchTerm(urlSearch);
        setDebouncedSearchTerm(urlSearch);
      }
      
      setCurrentPage(1);
    };

    handleUrlChange();
    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [category]);

  // Fetch Gigs
  const { data: gigsData, isLoading, isError } = useQuery({
    queryKey: ['gigs', category, debouncedSearchTerm, currentPage, filters.minPrice, filters.maxPrice, filters.keywords],
    queryFn: () => {
      const params = {
        category: category === 'all-services' ? undefined : category,
        search: debouncedSearchTerm,
        page: currentPage,
        limit: itemsPerPage
      };
      
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;
      if (filters.keywords) params.tags = filters.keywords;
      
      return getGigs(params);
    },
    keepPreviousData: true,
  });

  const gigs = gigsData?.gigs || [];
  const pagination = gigsData?.pagination || {};
  const totalPages = pagination.pages || 1;
  const totalGigs = pagination.total || 0;

  // Fetch Categories for tabs
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const categories = categoriesData?.categories || [];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Only set up sliding for the card being hovered
    let slidingInterval;
    if (activeHoverCard !== null) {
      slidingInterval = setInterval(() => {
        setSlidingIndexes(prevIndexes => {
          const newIndexes = { ...prevIndexes };
          const gig = gigs.find(g => g._id === activeHoverCard);
          // Use real checks for available thumbnails
          if (gig) {
             const availableThumbnails = [gig.cover, gig.firstThumbnail, gig.secondThumbnail, gig.thirdThumbnail].filter(Boolean);
             if (availableThumbnails.length > 1) {
                const currentIndex = prevIndexes[activeHoverCard] || 0;
                newIndexes[activeHoverCard] = (currentIndex + 1) % availableThumbnails.length;
             }
          }
          return newIndexes;
        });
      }, 1500); 
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (slidingInterval) clearInterval(slidingInterval);
    };
  }, [activeHoverCard, gigs]);

  // Get lowest price from pricing plans
  const getLowestPrice = (pricing) => {
    if (!pricing) return 0;
    const prices = [
      pricing.basic?.price,
      pricing.standard?.price,
      pricing.advanced?.price
    ].filter(p => p !== undefined && p !== null);
    
    if (prices.length === 0) return 0;
    return Math.min(...prices);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleCategoryClick = (cat) => {
    const slug = cat.slug || cat._id; // Use slug preferably
    navigate(`/category/${slug}`);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="w-full flex justify-center mt-8 mb-10">
        <div className={`flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
          <button 
            onClick={prevPage}
            className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full border border-gray-300 flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={isMobile ? 16 : 20} />
          </button>
          
          {currentPage > 1 && (
            <button 
              onClick={() => goToPage(1)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              1
            </button>
          )}
          
          {currentPage > 3 && <span className="px-1 text-gray-400">...</span>}
          
          {currentPage > 2 && (
            <button 
              onClick={() => goToPage(currentPage - 1)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button 
            className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full bg-blue-600 text-white flex items-center justify-center`}
          >
            {currentPage}
          </button>
          
          {currentPage < totalPages - 1 && (
            <button 
              onClick={() => goToPage(currentPage + 1)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              {currentPage + 1}
            </button>
          )}
          
          {currentPage < totalPages - 2 && <span className="px-1 text-gray-400">...</span>}
          
          {currentPage < totalPages && (
            <button 
              onClick={() => goToPage(totalPages)}
              className={`${isMobile ? 'w-6 h-6 text-xs' : 'w-10 h-10'} rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100`}
            >
              {totalPages}
            </button>
          )}
          
          <button 
            onClick={nextPage}
            className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={isMobile ? 16 : 20} />
          </button>
        </div>
      </div>
    );
  };

  // Category Tabs Component
  const CategoryTabs = () => {
    return (
      <div className="my-4 relative md:w-[60%] mx-auto">
        <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
            <div 
                className={`${isMobile ? 'min-w-[33%] px-1' : 'min-w-[16.666%] px-2'} flex-shrink-0 cursor-pointer`}
                onClick={() => navigate('/category/all-services')}
            >
               <div className={`py-2 px-3 text-center transition-colors rounded-[30px] bg-white hover:bg-gray-200 text-gray-700 ${
                  !category || category === 'all-services' ? 'border-[1px] border-[#185BC3]' : ''
                }`}>
                  <span className={`${isMobile ? 'text-[12px]' : 'text-[14px]'} font-regular truncate block`}>All Services</span>
                </div>
            </div>

            {categories.map((cat) => (
              <div 
                key={cat._id}
                className={`${isMobile ? 'min-w-[33%] px-1' : 'min-w-[16.666%] px-2'} flex-shrink-0 cursor-pointer`}
                onClick={() => handleCategoryClick(cat)}
              >
                <div className={`py-2 px-3 text-center   transition-colors rounded-[30px] bg-white hover:bg-gray-200 text-gray-700 ${
                  category === cat.slug ? 'border-[1px] border-[#185BC3]' : ''
                }`}>
                  <span className={`${isMobile ? 'text-[12px]' : 'text-[14px]'} font-regular truncate block`}>{cat.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
    );
  };

  // Service Card Component
  const ServiceCard = ({ gig }) => {
    const currentThumbnailIndex = slidingIndexes[gig._id] || 0;
    const lowestPrice = getLowestPrice(gig.pricing);
    
    // Gather all valid images
    const thumbnails = [gig.cover, gig.firstThumbnail, gig.secondThumbnail, gig.thirdThumbnail].filter(Boolean);
    const displayImage = thumbnails[currentThumbnailIndex] || gig.cover;

    // Generate initials for avatar fallback
    const getInitials = (name) => {
      if (!name) return "U";
      return name.charAt(0).toUpperCase();
    };
    
    // Creator name safe access
    const creatorName = gig.creator ? `${gig.creator.firstName || ''} ${gig.creator.lastName || ''}`.trim() || gig.creator.username : 'Unknown';

    return (
      <div 
        className="flex flex-col"
        onMouseEnter={() => setActiveHoverCard(gig._id)}
        onMouseLeave={() => setActiveHoverCard(null)}
      >
        <div className="relative rounded-lg overflow-hidden mb-3 hover:shadow-md transition-shadow duration-300">
          <a href={`/gigs/${gig.slug || gig._id}`}>
            {displayImage ? (
              <img 
                src={displayImage} 
                alt={gig.title} 
                className={`w-full ${isMobile ? 'h-36' : 'h-44'} object-cover transition-all duration-500`}
              />
            ) : (
              <div className={`w-full ${isMobile ? 'h-36' : 'h-44'} bg-gray-200 flex items-center justify-center`}>
                <span className="text-gray-500">No Image</span>
              </div>
            )}
          </a>
        
          {thumbnails.length > 1 && (
            <div className="absolute bottom-2 right-2 flex space-x-1">
              {thumbnails.map((_, index) => (
                <span 
                  key={index}
                  className={`${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'} bg-white rounded-full ${
                    index === currentThumbnailIndex ? 'opacity-100' : 'opacity-60'
                  }`}
                ></span>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center mb-2">
          {gig.creator && gig.creator.profilePicture ? (
            <img 
              src={gig.creator.profilePicture} 
              alt={creatorName} 
              className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full mr-2 object-cover`}
            />
          ) : (
            <div className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} rounded-full mr-2 bg-blue-600 flex items-center justify-center text-white text-xs`}>
              {getInitials(creatorName)}
            </div>
          )}
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>{creatorName}</span>
        </div>
        
        <a href={`/gigs/${gig.slug || gig._id}`} className="hover:text-blue-900">
          <h3 className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium mb-2 line-clamp-2`}>{gig.title}</h3>
        </a>
        
        <div className="flex items-center mb-1">
          <span className="text-[#000000]">★</span>
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ml-1`}>{(gig.totalStars / gig.starNumber || 0).toFixed(1)}</span>
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 ml-1`}>({gig.sales || 0})</span>
        </div>
        
        <div className="mt-auto">
          <p className={`${isMobile ? 'text-xs' : 'text-sm'}`}>From ${lowestPrice}</p>
        </div>
      </div>
    );
  };

  // Loading skeleton
  const LoadingSkeletons = () => (
    <div className={`grid ${isMobile ? 'grid-cols-2 gap-4' : 'grid-cols-4 gap-6'} mb-6`}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex flex-col animate-pulse">
           <div className={`bg-gray-200 rounded-lg ${isMobile ? 'h-36' : 'h-44'} mb-3`}></div>
           <div className="flex items-center mb-2">
             <div className="w-6 h-6 bg-gray-200 rounded-full mr-2"></div>
             <div className="h-4 bg-gray-200 w-24 rounded"></div>
           </div>
           <div className="h-4 bg-gray-200 w-full mb-2 rounded"></div>
           <div className="h-4 bg-gray-200 w-3/4 mb-2 rounded"></div>
           <div className="h-4 bg-gray-200 w-16 mt-auto rounded"></div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <Header/>
      <div className={`bg-[#F5F5F5] ${isMobile ? 'pb-6' : 'py-8'}`}>
        <div className={`${!isMobile ? 'max-w-7xl mx-auto px-4' : ''}`}>
          
          {/* Search Bar */}
          <div className={`relative ${isMobile ? 'px-4 w-[90%] mt-6' : 'w-[70%]'} mx-auto bg-[#F9F9FF] rounded-full overflow-hidden`}>
            <input
              type="text"
              placeholder="Search for service"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 focus:outline-none focus:ring-0 focus:ring-none focus:border-transparent text-gray-500"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search size={18} />
            </div>
            <button className="absolute right-2 top-1/2 transform bg-[#174568] -translate-y-1/2 text-white px-2 py-1 rounded-full">
              Go
            </button>
          </div>

          <div className={`${isMobile ? 'overflow-x-auto -mx-4 px-8 mb-4' : ''}`}>
            <CategoryTabs />
          </div>

          <div className={`${isMobile ? 'w-full bg-white px-4 py-5' : 'bg-white py-5 px-6 rounded-lg'}`}>
            <Actions/>
            
            <div className="flex justify-between items-center mb-6">
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-3xl'} font-medium md:font-bold text-[#666464]`}>
                {categoryTitle}
              </h1>
              <div className="flex items-center">
                <p className="text-[#174568] mr-4">{totalGigs} services</p>
              </div>
            </div>

            {isLoading ? (
              <LoadingSkeletons />
            ) : gigs.length === 0 ? (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-700 mb-2">No services found</h3>
                <p className="text-gray-500">Try adjusting your search or filter to find what you're looking for.</p>
              </div>
            ) : (
              <>
                <div className={`grid ${isMobile ? 'grid-cols-2 gap-4' : 'grid-cols-4 gap-6'} mb-6`}>
                  {gigs.map(gig => (
                    <ServiceCard key={gig._id} gig={gig} />
                  ))}
                </div>
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </div>
     <CustomQuoteBanner/>
      <Footer/>
    </>
  );
};

export default CategoryShow;